import React, { useRef, useEffect, useState } from 'react';
import { Search, Filter, Eye, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { providerTypesService, SupportedProviderType } from '../../services/providerTypes';

interface DNSZone {
  id: string;
  name: string;
  providerId: string;
  providerName: string;
  providerType?: string;
}

interface ZonesFiltersProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  selectedProvider: string;
  onProviderChange: (providerId: string) => void;
  showProviderFilter: boolean;
  onToggleProviderFilter: () => void;
  zones: DNSZone[];
  filteredZones: DNSZone[];
}

const ZonesFilters: React.FC<ZonesFiltersProps> = ({
  searchTerm,
  onSearchChange,
  selectedProvider,
  onProviderChange,
  showProviderFilter,
  onToggleProviderFilter,
  zones,
  filteredZones,
}) => {
  const providerFilterRef = useRef<HTMLDivElement>(null);
  const [showAllProviders, setShowAllProviders] = useState(false);
  const [supportedProviders, setSupportedProviders] = useState<SupportedProviderType[]>([]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (providerFilterRef.current && !providerFilterRef.current.contains(event.target as Node)) {
        onToggleProviderFilter();
      }
    };

    if (showProviderFilter) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProviderFilter, onToggleProviderFilter]);

  // Load all supported providers when component mounts
  useEffect(() => {
    const loadSupportedProviders = async () => {
      try {
        const providers = await providerTypesService.getSupportedProviderTypes();
        setSupportedProviders(providers);
      } catch (error) {
        console.error('Failed to load supported providers:', error);
      }
    };
    loadSupportedProviders();
  }, []);

  const getProviderIcon = (type: string | undefined) => {
    switch (type?.toLowerCase()) {
      case 'cloudflare':
        return '🔶';
      case 'route53':
      case 'aws':
        return '🟠';
      case 'azure':
        return '🔵';
      case 'gcp':
      case 'google':
        return '🟢';
      case 'digitalocean':
        return '🌊';
      default:
        return '🛡️';
    }
  };

  // Get unique providers for filtering
  const uniqueProviders = Array.from(
    new Set(
      zones.map((zone) => ({
        id: zone.providerId,
        name: zone.providerName,
        type: zone.providerType,
      })),
    ),
  ).filter((provider, index, self) => index === self.findIndex((p) => p.id === provider.id));

  // Get all providers to display (configured + all supported)
  const displayProviders = showAllProviders
    ? [
        ...uniqueProviders.map(p => ({ ...p, isConfigured: true })),
        ...supportedProviders
          .filter(sp => !uniqueProviders.some(up => up.type === sp.type))
          .map(sp => ({
            id: `unsupported-${sp.type}`,
            name: sp.name,
            type: sp.type,
            isConfigured: false
          }))
      ]
    : uniqueProviders.map(p => ({ ...p, isConfigured: true }));

  return (
    <motion.div
      className="flex items-center justify-between space-x-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <div className="flex-1 relative max-w-md">
        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search zones, domains, providers..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="form-input pl-10 pr-4"
        />
      </div>

      <div className="flex items-center space-x-3">
        <div className="relative" ref={providerFilterRef}>
          <motion.button
            className={`inline-flex items-center justify-center p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded-lg transition-colors focus:outline-none ${selectedProvider !== 'all' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : ''}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onToggleProviderFilter}
          >
            <Filter className="w-4 h-4" />
          </motion.button>

          <AnimatePresence>
            {showProviderFilter && (
              <motion.div
                className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-slate-900 dark:text-white">
                      Filter by DNS Provider
                    </h4>
                    <button
                      onClick={() => setShowAllProviders(!showAllProviders)}
                      className="inline-flex items-center text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                      title={showAllProviders ? 'Show only configured providers' : 'Show all supported providers'}
                    >
                      {showAllProviders ? (
                        <>
                          <EyeOff className="w-3 h-3 mr-1" />
                          Configured Only
                        </>
                      ) : (
                        <>
                          <Eye className="w-3 h-3 mr-1" />
                          Show All
                        </>
                      )}
                    </button>
                  </div>
                  <div className="space-y-1">
                    <button
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        selectedProvider === 'all'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}
                      onClick={() => onProviderChange('all')}
                    >
                      All Providers ({zones.length})
                    </button>
                    {displayProviders.map((provider) => {
                      const providerZoneCount = zones.filter(
                        (z) => z.providerId === provider.id,
                      ).length;
                      const isConfigured = 'isConfigured' in provider ? provider.isConfigured : true;
                      return (
                        <button
                          key={provider.id}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedProvider === provider.id
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                              : isConfigured
                              ? 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400'
                          } ${
                            !isConfigured ? 'opacity-70' : ''
                          }`}
                          onClick={() => isConfigured ? onProviderChange(provider.id) : null}
                          disabled={!isConfigured}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span>{getProviderIcon(provider.type)}</span>
                              <span className="flex items-center space-x-1">
                                <span>{provider.name}</span>
                                {!isConfigured && (
                                  <span className="text-xs text-slate-400 dark:text-slate-500">
                                    (not configured)
                                  </span>
                                )}
                              </span>
                            </div>
                            <span className="text-xs text-slate-500">
                              ({providerZoneCount})
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="text-body-small text-slate-500 dark:text-slate-400">
          {filteredZones.length} of {zones.length} zones
          {selectedProvider !== 'all' && (
            <div className="mt-1 flex items-center space-x-2">
              <span className="text-blue-600 dark:text-blue-400 text-xs">Filtered by:</span>
              <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs font-medium">
                {(() => {
                  const provider = uniqueProviders.find((p) => p.id === selectedProvider);
                  return (
                    <>
                      <span>{getProviderIcon(provider?.type)}</span>
                      <span>{provider?.name}</span>
                    </>
                  );
                })()}
              </span>
              <button
                onClick={() => onProviderChange('all')}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-xs"
                title="Clear filter"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ZonesFilters;
